import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import type { PreviewResult, PreviewStats } from '../../../../schemas/provinceConversion';

export interface ConversionPreviewPanelProps {
	preview: PreviewResult | undefined;
	isLoading: boolean;
	isError: boolean;
	isApplying: boolean;
	onApply: () => void;
}

const STAT_LABELS: Array<{ key: keyof PreviewStats; label: string }> = [
	{ key: 'soRename', label: 'Đổi mã' },
	{ key: 'soMerge', label: 'Sáp nhập' },
	{ key: 'soTinhTruoc', label: 'Số tỉnh trước' },
	{ key: 'soTinhSau', label: 'Số tỉnh sau' },
	{ key: 'soTruongAnhHuong', label: 'Số trường ảnh hưởng' },
	{ key: 'soDinhTruoc', label: 'Số đỉnh trước' },
	{ key: 'soDinhSau', label: 'Số đỉnh sau' },
];

const DEFAULT_BLOCK_REASON = 'Còn lỗi cần khắc phục trước khi áp dụng.';

export function ConversionPreviewPanel({ preview, isLoading, isError, isApplying, onApply }: ConversionPreviewPanelProps) {
	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" p={4}>
				<CircularProgress />
			</Box>
		);
	}

	if (isError) {
		return <Alert severity="error">Không thể tính toán bản xem trước.</Alert>;
	}

	if (!preview) {
		return <Alert severity="info">Chưa có kết quả xem trước. Nhấn &quot;Xem trước&quot; để bắt đầu.</Alert>;
	}

	// `Tooltip` không hiển thị khi title rỗng — dùng chuỗi rỗng khi hopLe để
	// nút không có tooltip thừa lúc đã hợp lệ.
	const blockReason = preview.hopLe ? '' : preview.loi[0]?.thongDiep ?? DEFAULT_BLOCK_REASON;

	return (
		<Stack spacing={2}>
			<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
				{STAT_LABELS.map(({ key, label }) => (
					<Chip key={key} label={`${label}: ${preview.thongKe[key]}`} variant="outlined" />
				))}
			</Stack>

			{preview.canhBao.length > 0 && (
				<Alert severity="warning">
					<Stack component="ul" sx={{ m: 0, pl: 2 }}>
						{preview.canhBao.map((warning, index) => (
							<Typography component="li" variant="body2" key={index}>
								{warning}
							</Typography>
						))}
					</Stack>
				</Alert>
			)}

			{preview.loi.length > 0 && (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Mã</TableCell>
								<TableCell>Dòng</TableCell>
								<TableCell>Lỗi</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{preview.loi.map((error, index) => (
								<TableRow key={`${error.ma}-${index}`}>
									<TableCell>{error.ma}</TableCell>
									<TableCell>{error.dong ?? '—'}</TableCell>
									<TableCell>{error.thongDiep}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Box>
				<Tooltip title={blockReason} arrow>
					<span>
						<Button variant="contained" color="primary" disabled={!preview.hopLe || isApplying} onClick={onApply}>
							{isApplying ? 'Đang áp dụng...' : 'Áp dụng'}
						</Button>
					</span>
				</Tooltip>
			</Box>
		</Stack>
	);
}
